import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React, { useEffect } from "react"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getProp from "@insite/mobius/utilities/getProp";
import { NoTranslateInjector } from "@store/Components/Injectors/NoTranslateInjector";
import { COLORS } from "@store/theme/common/design";
import { usePrevious } from "@store/utilities/hooks";
import React, { useEffect, useState } from "react";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // Store Custom Code
    location: getLocation(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // Store Custom Code
    header?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    /* Store Custom Code - Start */
    header: {
        css: css`
            > div {
                position: relative;
                top: inherit;
                width: 100%;
                background-color: ${COLORS.white};
                z-index: ${getProp("theme.zIndex.header")};

                &.sticky-header {
                    position: fixed;
                    top: 0;
                }

                &.short-screen {
                    bottom: 0;
                    overflow-y: auto;
                }
            }
        `,
    },
    /* Store Custom Code - End */
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // Store Custom Code
    location,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    /* Store Custom Code - Start */
    const header = React.useRef<any>();
    const headerContainer = React.useRef<any>();
    const [canTrack, setCanTrack] = useState(false);
    const prevUrlState = usePrevious(location.pathname);
    const stickyHeaderClass = "sticky-header";
    const shortScreenClass = "short-screen";

    useEffect(() => {
        window.addEventListener("resize", setSticky, true);
        window.addEventListener("scroll", setSticky, true);
        setSticky();
        setCanTrack(true);
    }, []);

    const setSticky = () => {
        const stickyTopHeader = document.querySelector("header .topHeaderSection") as HTMLElement;
        const stickyTopHeaderHeight = stickyTopHeader ? stickyTopHeader.clientHeight : 0;
        const headerClassList = header.current.classList;
        const currentHeight: number = header?.current?.clientHeight;
        const wrapperSelector = document.querySelector("main, header + div") as HTMLElement;
        const contentHeight: number = headerContainer?.current?.clientHeight;

        if (window.pageYOffset >= stickyTopHeaderHeight) {
            if (!headerClassList.contains(stickyHeaderClass)) {
                headerClassList.add(stickyHeaderClass);
                wrapperSelector.style.display = "block";
                header.current.style.marginTop = `-${stickyTopHeaderHeight}px`;
            }
            wrapperSelector.style.paddingTop = `${currentHeight}px`;
        } else {
            headerClassList.remove(stickyHeaderClass);
            header.current.style.marginTop = `0`;
            wrapperSelector.style.paddingTop = `0`;
        }

        if (contentHeight > window.innerHeight) {
            if (!headerClassList.contains(shortScreenClass)) {
                headerClassList.add(shortScreenClass);
            }
        } else {
            headerClassList.remove(shortScreenClass);
        }
    };

    if (prevUrlState !== location.pathname && canTrack) {
        setSticky();
    }
    /* Store Custom Code - End */

    return (
        <>
            {/* Store Custom Code - Start */}
            <NoTranslateInjector />
            <Page as="header" isHeader={true} {...styles.header}>
                <StyledWrapper as="div" ref={header}>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    {/* Store Custom Code - Removed EagerLoadingLogoProvider */}
                    <StyledWrapper ref={headerContainer} /* Store Custom Code */>
                        <Zone contentId={id} zoneName="Breadcrumb" />
                    </StyledWrapper>
                    <span ref={afterHeader} tabIndex={-1} />
                </StyledWrapper>
            </Page>
            {/* Store Custom Code - End */}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
